import {
  type BaseDataMessage,
  type ToolCallGenericMessage,
  DataMessageTypeEnum,
} from '@terra/shared-dto-chat-types';
import {
  isCoderToolNameForMerge,
  mergeCoderToolOutputValue,
} from '../trace-view/utils/coder-tool-output';

export const TOOL_CALL_STATUS_PRIORITY: Record<string, number> = {
  started: 0,
  'in-progress': 1,
  completed: 2,
  result: 2,
  error: 2,
  aborted: 2,
};

export function isToolCallData(
  data: BaseDataMessage,
): data is ToolCallGenericMessage {
  return (
    data.type === DataMessageTypeEnum.ToolCallStart ||
    data.type === DataMessageTypeEnum.ToolCallUpdate ||
    data.type === DataMessageTypeEnum.ToolCallComplete ||
    data.type === DataMessageTypeEnum.ToolCallError
  );
}

export function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function resolveMergedToolCallOutput({
  anchor,
  update,
  toolName,
}: {
  anchor: ToolCallGenericMessage;
  update: ToolCallGenericMessage;
  toolName: string | undefined;
}): ToolCallGenericMessage['output'] {
  const updateHasOutput = hasValue(update.output);
  const anchorHasOutput = hasValue(anchor.output);

  if (!updateHasOutput) {
    return anchor.output;
  }

  if (!anchorHasOutput) {
    return update.output;
  }

  if (isCoderToolNameForMerge(toolName)) {
    return mergeCoderToolOutputValue({
      anchorOutput: anchor.output,
      updateOutput: update.output,
    });
  }

  return update.output;
}

export function mergeToolCallData({
  anchor,
  update,
}: {
  anchor: ToolCallGenericMessage;
  update: ToolCallGenericMessage;
}): ToolCallGenericMessage {
  const anchorInput = hasValue(anchor.input) ? anchor.input : undefined;
  const updateInput = hasValue(update.input) ? update.input : undefined;
  const nextInput = anchorInput ?? updateInput;
  const toolName = update.toolName ?? anchor.toolName;
  const mergedOutput = resolveMergedToolCallOutput({
    anchor,
    update,
    toolName,
  });

  return {
    ...anchor,
    ...update,
    input: nextInput,
    output: mergedOutput,
    error: hasValue(update.error) ? update.error : anchor.error,
    timing: hasValue(update.timing) ? update.timing : anchor.timing,
    progressEvents: hasValue(update.progressEvents)
      ? update.progressEvents
      : anchor.progressEvents,
  };
}
